import React, { useState, useRef, useEffect } from 'react';
import { Modal, InputNumber, Button, Row, Col, Typography, Space, Alert, Table, Tabs, Tag } from 'antd';
import { PrinterOutlined, EyeOutlined, QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import QRCode from 'react-qr-code';
import { useReactToPrint } from 'react-to-print';
import { useConfig } from '../../../context/ConfigContext';

const { Title, Text, Paragraph } = Typography;

const ROW_WIDTH_MM = 100.2;
const LABEL_WIDTH_MM = 32.07;
const LABEL_HEIGHT_MM = 16;
const GAP_MM = 2;
const QR_SIZE_MM = 13;

const GuiaImpresion = ({ open, onClose }) => {
    const pasosMac = [
        {
            titulo: '1. Abrir el diálogo del sistema directamente',
            texto: 'Al hacer clic en "Imprimir" en la app, en la ventana que aparece presiona Cmd + Alt + P para saltar directo al diálogo nativo de macOS.'
        },
        {
            titulo: '2. Elegir la impresora',
            texto: 'En "Impresora", selecciona tu impresora de etiquetas (ej: 4BARCODE 4B-2054TA).'
        },
        {
            titulo: '3. Elegir el tamaño de papel',
            texto: 'En "Tamaño del papel" busca el preset "rollo 102x16" (102 x 16 mm). Si no existe, créalo: Administrar tamaños personalizados... -> Ancho 101,2 mm, Alto 16 mm, márgenes en 0.'
        },
        {
            titulo: '4. Orientación y escala',
            texto: 'Orientación: Horizontal. Escala: 100%.'
        },
        {
            titulo: '5. Imprimir',
            texto: 'Haz clic en "Imprimir". macOS recuerda esta configuración.'
        }
    ];

    const pasosWindows = [
        {
            titulo: '1. Abrir el diálogo del sistema directamente',
            texto: 'En la ventana de impresión, presiona Ctrl + Shift + P para abrir el diálogo nativo.'
        },
        {
            titulo: '2. Elegir la impresora',
            texto: 'Selecciona tu impresora de etiquetas en la lista.'
        },
        {
            titulo: '3. Configurar el tamaño en el driver',
            texto: 'Haz clic en "Preferencias" y selecciona un tamaño de 102 x 16 mm en la sección de papel.'
        },
        {
            titulo: '4. Orientación y escala',
            texto: 'Orientación: Horizontal. Escala: 100% / "Tamaño real".'
        },
        {
            titulo: '5. Imprimir',
            texto: 'Confirma y haz clic en "Imprimir".'
        }
    ];

    const renderPasos = (pasos) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {pasos.map((p, i) => (
                <div key={i}>
                    <Text strong style={{ fontSize: '14px', color: '#0f172a' }}>{p.titulo}</Text>
                    <Paragraph style={{ margin: '4px 0 0 0', color: '#475569', fontSize: '13px' }}>{p.texto}</Paragraph>
                </div>
            ))}
        </div>
    );

    return (
        <Modal
            title={<Space><QuestionCircleOutlined /> ¿Cómo imprimir correctamente?</Space>}
            open={open}
            onCancel={onClose}
            footer={[<Button key="ok" type="primary" onClick={onClose} style={{ background: '#22BABB' }}>Entendido</Button>]}
            width={620}
        >
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: '16px' }}
                message="Por qué es necesario"
                description="Las impresoras térmicas necesitan un tamaño de papel exacto configurado a nivel de sistema. El diálogo rápido del navegador no siempre respeta ese tamaño."
            />
            <Tabs
                defaultActiveKey="mac"
                items={[
                    { key: 'mac', label: <span>macOS</span>, children: renderPasos(pasosMac) },
                    { key: 'win', label: <span>Windows</span>, children: renderPasos(pasosWindows) },
                ]}
            />
        </Modal>
    );
};

const ModalImprimirEtiquetas = ({ visible, onClose, productos = [] }) => {
    const { config } = useConfig();
    const formatoEtiqueta = config.settings?.formato_etiqueta || '30x15_3col';
    const multiplo = formatoEtiqueta.includes('3col') ? 3 : 1;

    const [cantidades, setCantidades] = useState({});
    const [guiaVisible, setGuiaVisible] = useState(false);
    const printRef = useRef(null);

    useEffect(() => {
        if (visible && productos.length > 0) {
            const countsIniciales = {};
            productos.forEach(p => countsIniciales[p.id] = multiplo);
            setCantidades(countsIniciales);
        }
    }, [visible, productos, multiplo]);

    const handleCantidadChange = (id, valor) => {
        setCantidades(prev => ({ ...prev, [id]: valor || 0 }));
    };

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Etiquetas_Yugonub`,
        pageStyle: `
            @page { 
                size: ${ROW_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm !important; 
                margin: 0 !important; 
            }
            html, body {
                margin: 0 !important;
                padding: 0 !important;
                background-color: #ffffff !important;
            }
            * {
                /* Anula cualquier caché del CSS viejo */
                visibility: visible !important; 
                box-sizing: border-box !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }
            /* Evita que el contenido se salga del margen de los 16mm */
            #etiquetas-print-area {
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            svg, img {
                image-rendering: pixelated;
                shape-rendering: crispEdges !important;
            }
        `
    });

    if (!visible || productos.length === 0) return null;

    const etiquetasRenderizar = [];
    productos.forEach(prod => {
        const copias = cantidades[prod.id] || 0;
        for (let i = 0; i < copias; i++) {
            etiquetasRenderizar.push(prod);
        }
    });

    const filasEtiquetas = [];
    for (let i = 0; i < etiquetasRenderizar.length; i += 3) {
        filasEtiquetas.push(etiquetasRenderizar.slice(i, i + 3));
    }

    const totalEtiquetas = etiquetasRenderizar.length;

    const columns = [
        {
            title: 'Producto',
            dataIndex: 'nombre',
            key: 'nombre',
            render: (text, record) => {
                const cod = record.codigo_barras || record.codigoBarras || record.codigo_interno || record.codigoInterno || 'Sin Código';
                return (
                    <div style={{ lineHeight: '1.2' }}>
                        <Text strong style={{ fontSize: '13px' }}>{text}</Text><br />
                        <Text type="secondary" style={{ fontSize: '11px' }}>{cod}</Text>
                    </div>
                );
            }
        },
        {
            title: 'Copias',
            key: 'copias',
            width: 140,
            render: (_, record) => (
                <Space.Compact style={{ display: 'flex', textAlignLast: 'center' }}>
                    <Button style={{ fontWeight: '700', fontSize: '12px' }} onClick={() => handleCantidadChange(record.id, Math.max(0, (cantidades[record.id] || 0) - multiplo))}>-</Button>
                    <InputNumber
                        min={0}
                        max={100}
                        step={multiplo}
                        value={cantidades[record.id]}
                        onChange={(val) => handleCantidadChange(record.id, val)}
                        controls={false}
                        style={{ width: '50px', textAlign: 'center' }}
                        onFocus={(e) => e.target.select()}
                    />
                    <Button style={{ fontWeight: '700', fontSize: '12px' }} onClick={() => handleCantidadChange(record.id, (cantidades[record.id] || 0) + multiplo)}>+</Button>
                </Space.Compact>
            )
        }
    ];

    const Etiqueta = ({ prod }) => {
        const codigoValido = String(prod.codigo_barras || prod.codigoBarras || prod.codigo_interno || prod.codigoInterno || '000000000000');
        const nombreValido = prod.nombre ? String(prod.nombre).toUpperCase() : 'PRODUCTO';
    
        return (
            <div style={{
                width: `${LABEL_WIDTH_MM}mm`,
                height: `${LABEL_HEIGHT_MM}mm`,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start', /* CRÍTICO: Pega el QR y el texto a la parte superior */
                boxSizing: 'border-box',
                overflow: 'hidden',
                /* MÁRGENES: Arriba 0.5mm | Derecha 1mm | Abajo 2mm | Izquierda 2.5mm */
                padding: '0.5mm 1mm 2mm 2.5mm', 
                gap: '1.5mm'
            }}>
                {/* QR a la izquierda */}
                <div style={{
                    width: `${QR_SIZE_MM}mm`,
                    height: `${QR_SIZE_MM}mm`,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'flex-start', /* Mantiene el QR arriba */
                    justifyContent: 'center'
                }}>
                    <QRCode
                        value={codigoValido}
                        size={128}
                        level="M"
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                        style={{ width: '100%', height: '100%' }}
                    />
                </div>
    
                {/* Texto a la derecha */}
                <div style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start', /* Mantiene el texto arriba */
                    overflow: 'hidden',
                    paddingTop: '0.5mm' /* Pequeño respiro superior para alinear con el QR */
                }}>
                    <span style={{
                        fontSize: '6.5px',
                        fontWeight: 'bold',
                        lineHeight: 1.15,
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontFamily: 'sans-serif',
                        color: '#000',
                        wordBreak: 'break-word'
                    }}>
                        {nombreValido}
                    </span>
                    <span style={{
                        fontSize: '6px',
                        lineHeight: 1.2,
                        marginTop: '0.5mm',
                        color: '#000',
                        fontFamily: 'monospace',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {codigoValido}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <Modal
            title={<Space><PrinterOutlined /> Imprimir Etiquetas ({totalEtiquetas} en total)</Space>}
            open={visible}
            onCancel={onClose}
            width={900}
            footer={[
                <Button key="cancel" onClick={onClose}>Cancelar</Button>,
                <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint} style={{ background: '#22BABB' }} disabled={totalEtiquetas === 0}>
                    IMPRIMIR {totalEtiquetas} ETIQUETAS
                </Button>
            ]}
        >
            <Row gutter={24} style={{ marginTop: '20px' }}>
                <Col xs={24} md={10} style={{ display: 'flex', flexDirection: 'column', height: '400px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={5} style={{ margin: 0 }}>Configuración de Lote</Title>
                        <Button
                            type="link"
                            size="small"
                            icon={<QuestionCircleOutlined />}
                            onClick={() => setGuiaVisible(true)}
                            style={{ padding: 0 }}
                        >
                            ¿Cómo imprimir?
                        </Button>
                    </div>
                    <Table
                        dataSource={productos}
                        columns={columns}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        scroll={{ y: 250 }}
                        style={{ border: '1px solid #f1f5f9', borderRadius: '8px', marginTop: '8px' }}
                    />
                    <Alert
                        message="Recuerda usar el diálogo del sistema"
                        description={
                            <span>
                                Presiona <Tag>Cmd+Alt+P</Tag> (Mac) o <Tag>Ctrl+Shift+P</Tag> (Windows) al imprimir. Toca "¿Cómo imprimir?" arriba para ver la guía completa.
                            </span>
                        }
                        type="warning"
                        showIcon
                        style={{ marginTop: '12px' }}
                    />
                </Col>

                <Col xs={24} md={14}>
                    <Title level={5}><EyeOutlined /> Vista Previa del Rollo</Title>
                    <div style={{
                        background: '#f8fafc', padding: '20px', borderRadius: '8px',
                        border: '1px solid #cbd5e1', height: '370px', overflowY: 'auto',
                        display: 'flex', flexDirection: 'column', alignItems: 'center'
                    }}>
                        {filasEtiquetas.map((fila, rowIndex) => (
                            <div key={rowIndex} style={{
                                display: 'flex',
                                flexWrap: 'nowrap',
                                width: `${ROW_WIDTH_MM}mm`,
                                gap: `${GAP_MM}mm`,
                                justifyContent: 'flex-start',
                                backgroundColor: '#fff',
                                marginBottom: '2mm',
                                border: '1px dashed #cbd5e1'
                            }}>
                                {fila.map((prod, colIndex) => (
                                    <Etiqueta key={colIndex} prod={prod} />
                                ))}
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>

            <GuiaImpresion open={guiaVisible} onClose={() => setGuiaVisible(false)} />

            {/* ÁREA OCULTA PARA IMPRESIÓN DIRECTA DEL NAVEGADOR */}
            <div style={{ position: 'absolute', top: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                <div ref={printRef} id="etiquetas-print-area">
                    {filasEtiquetas.map((fila, rowIndex) => (
                        <div
                            key={rowIndex}
                            style={{
                                width: `${ROW_WIDTH_MM}mm`,
                                height: `${LABEL_HEIGHT_MM}mm`,
                                display: 'flex',
                                flexWrap: 'nowrap',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: `${GAP_MM}mm`,
                                backgroundColor: '#fff',
                                margin: 0,
                                padding: 0,
                                overflow: 'hidden',
                                pageBreakAfter: 'always',
                                pageBreakInside: 'avoid'
                            }}
                        >
                            {fila.map((prod, colIndex) => (
                                <Etiqueta key={colIndex} prod={prod} />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};

export default ModalImprimirEtiquetas;